import { BackgroundSyncPlugin } from "workbox-background-sync";
import { clientsClaim } from "workbox-core";
import { cleanupOutdatedCaches, precacheAndRoute } from "workbox-precaching";
import { registerRoute } from "workbox-routing";
import {
  CacheFirst,
  NetworkFirst,
  NetworkOnly,
  StaleWhileRevalidate,
} from "workbox-strategies";

declare let self: ServiceWorkerGlobalScope & {
  __WB_MANIFEST: Array<import("workbox-build").ManifestEntry>;
};

clientsClaim();

cleanupOutdatedCaches();

precacheAndRoute(self.__WB_MANIFEST || []);

const MAX_CACHE_ENTRIES = 50; // Maximum number of entries to keep in the cache
const MAX_CACHE_AGE_SECONDS = 30 * 24 * 60 * 60; // Maximum age of cache entries in seconds (30 days)

// Function to limit cache size
const limitCacheSize = async (cacheName: string, maxEntries: number) => {
  const cache = await caches.open(cacheName);
  const keys = await cache.keys();
  if (keys.length > maxEntries) {
    await cache.delete(keys[0]);
    limitCacheSize(cacheName, maxEntries); // Recursively clean up
  }
};

// Register network only routes with background sync support
const networkOnlyRoutes = [
  "/insiders/api/select/invoice-fiscal-collection",
  "/insiders/api/select/loadexrate_pkey",
  "/rest/api/persist",
  "/rest/api/logins/whoami",
];

const networkOnlyByString = ["_pkey", "Pkey"];

const registerNetworkOnlyRoute = (route: string) => {
  try {
    if ("sync" in self.registration) {
      const bgSyncPlugin = new BackgroundSyncPlugin("bgSyncQueue", {
        maxRetentionTime: 24 * 60, // Retry for 24 hours
      });

      registerRoute(
        ({ url }) => url.pathname.startsWith(route),
        new NetworkOnly({ plugins: [bgSyncPlugin] })
      );
    } else {
      throw new Error("SyncManager not supported");
    }
  } catch (error) {
    console.warn("Background Sync not supported:", error);
    registerRoute(
      ({ url }) => url.pathname.startsWith(route),
      new NetworkOnly()
    );
  }
};

networkOnlyRoutes.forEach(registerNetworkOnlyRoute);

networkOnlyByString.forEach((substring) => {
  registerRoute(
    ({ url }) => url.pathname.includes(substring),
    new NetworkOnly()
  );
});

const cacheFirstRoutes = ["/rest/api/select/ss_iso3166", "/assets/monede"];

cacheFirstRoutes.forEach((route) => {
  registerRoute(
    ({ url }) => url.pathname.startsWith(route),
    new CacheFirst({
      cacheName: "static-data-cache",
      plugins: [
        {
          cacheDidUpdate: async () => {
            await limitCacheSize("static-data-cache", MAX_CACHE_ENTRIES);
          },
        },
      ],
    })
  );
});

const staleWhileRevalidateRoutes = [
  "/insiders/api/select/loadexrate_range",
  "/select/ss_mywork_criteria",
  "/db/dbof_emitenti",
];

staleWhileRevalidateRoutes.forEach((route) => {
  registerRoute(
    ({ url }) => url.pathname.startsWith(route),
    new StaleWhileRevalidate({
      cacheName: "dynamic-data-cache",
      plugins: [
        {
          cacheDidUpdate: async () => {
            await limitCacheSize("dynamic-data-cache", MAX_CACHE_ENTRIES);
          },
        },
      ],
    })
  );
});

registerRoute(
  ({ request }) =>
    ["document", "script", "style", "image"].includes(request.destination),
  new NetworkFirst({
    cacheName: "default-cache",
    plugins: [
      {
        cacheDidUpdate: async () => {
          await limitCacheSize("default-cache", MAX_CACHE_ENTRIES);
        },
      },
    ],
  })
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/admin-sw.js")
      .then((registration) => {
        console.log(
          "Service Worker registered with scope:",
          registration.scope
        );
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  });
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      if (registration.active && registration.scope.includes("sw.js")) {
        registration.unregister().then(() => {
          console.log("Old service worker unregistered:", registration.scope);
        });
      }
    });
  });
}

self.addEventListener("activate", (event) => {
  event.waitUntil(
    caches.keys().then((cacheNames) => {
      return Promise.all(
        cacheNames.map((cacheName) => {
          if (cacheName !== "static-resources") {
            return caches.delete(cacheName);
          }
        })
      );
    })
  );
});
